import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { Modal } from '@symphenia/ui-components'
import {
  Plus, Pencil, Trash2, CalendarOff, Settings, Check,
  ArrowUp, ArrowDown, ArrowUpDown, Download, Printer,
} from 'lucide-react'
import { fmtDateLong } from '@/lib/format-date'
import { erfuelltRecht } from '@symphenia/shared-types'
import { useHoursFormat, formatHoursWithUnit, type HoursFormat } from '@/lib/hours-format'
import { useAuthStore } from '@/lib/auth.store'
import { usersApi } from '@/lib/users/users.api'
import type { User } from '@/lib/users/user.types'
import { useSearchParams } from 'react-router-dom'
import { FilterBar, filterAusAdresse } from '@/components/list/FilterBar'
import { HelpPopover } from '@/components/ui/HelpPopover'
import { HILFE_VERWEISE } from '@/lib/hilfe'
import { loadUiSetting, markUiSettingApplied, saveUiSetting, saveUiSettingIfChanged } from '@/lib/ui-settings'
import { LayoutDefaultActions } from '@/components/ui/LayoutDefaultActions'
import type { FilterFieldDef, ActiveFilter, ColumnFieldDef, ColumnLayout, ColumnSortConfig } from '@/components/list/list.types';
import { absencesApi, absenceTypesApi } from '../api/absence.api'
import type { Absence, AbsenceType } from '../types/absence.types'
import { pageWide } from '@/lib/form-styles'
import { bestaetige } from '@/lib/dialoge'

// AP-30-D: „Sieht er fremde Fehlzeiten?" ist eine Frage nach dem
// Geltungsbereich, nicht nach der Rolle — `time.absence.read.all` gegen
// `.own`. Die Rollenliste hier ging an „Nur Lesen" vorbei, das alle
// Fehlzeiten sieht und in keiner dieser drei Rollen steht.

// Alle Fehlzeiten werden in einem Rutsch geladen (kleiner Datensatz) und clientseitig
// gefiltert/sortiert/summiert/exportiert. Backend deckelt limit bei 1000.
const LOAD_LIMIT = 1000

// Dauer gemäß tenant-weitem Stunden-Format (Dezimal ohne überflüssige Nullen / H:MM)
function formatMinutes(minutes: number, format: HoursFormat = 'decimal'): string {
  return formatHoursWithUnit(minutes, format)
}

function todayStr(): string {
  // Lokales Datum (Wall-Clock) als YYYY-MM-DD
  const d = new Date()
  const y = d.getFullYear()
  const mo = String(d.getMonth() + 1).padStart(2, '0')
  const da = String(d.getDate()).padStart(2, '0')
  return `${y}-${mo}-${da}`
}

// ─── Spaltenlayout (AP-23: Cache + Server) ──────────────────────────────────────

const COLUMN_LAYOUT_KEY = 'columnbar_layout_absences'
const PAGE_SIZE_KEY = 'pagesize_absences'

const DEFAULT_COLUMN_KEYS = ['employee', 'absenceType', 'duration', 'note']

/** Vorbelegung der FilterBar: Zeitraum + Art der Fehlzeit (es gibt hier keinen Status). */
const DEFAULT_FILTER_FIELD_KEYS = ['dateFrom', 'dateTo', 'absenceType']

/** Alle Spalten sind clientseitig sortierbar. */
const SORTABLE_KEYS = new Set(['date', 'employee', 'absenceType', 'duration', 'note'])

const COLUMN_CATEGORY_LABELS: Record<string, string> = {
  core: 'Kerndaten',
  absence: 'Fehlzeit',
  time: 'Zeitdaten',
}

// AP-23: synchron aus dem Cache lesen, Cache + API (debounced) schreiben.
function loadColumnLayout(): ColumnLayout {
  const parsed = loadUiSetting<ColumnLayout | null>(COLUMN_LAYOUT_KEY, null)
  const columnKeys = (parsed?.columnKeys ?? []).filter((k) => k !== 'date')
  if (columnKeys.length > 0) {
    return { columnKeys, columnWidths: parsed?.columnWidths ?? {}, sortConfig: parsed?.sortConfig ?? null }
  }
  return { columnKeys: DEFAULT_COLUMN_KEYS, columnWidths: {}, sortConfig: null }
}

function saveColumnLayout(layout: ColumnLayout) {
  saveUiSettingIfChanged(COLUMN_LAYOUT_KEY, layout)
}

function buildAvailableColumns(isManager: boolean): ColumnFieldDef[] {
  const cols: ColumnFieldDef[] = []
  if (isManager) cols.push({ key: 'employee', label: 'Mitarbeiter', category: 'core' })
  cols.push({ key: 'absenceType', label: 'Typ', category: 'absence' })
  cols.push({ key: 'duration', label: 'Dauer', category: 'time', summable: true, align: 'right' })
  cols.push({ key: 'note', label: 'Notiz', category: 'core' })
  return cols
}

function buildAvailableFilterFields(isManager: boolean, users: User[], types: AbsenceType[]): FilterFieldDef[] {
  const fields: FilterFieldDef[] = []
  // KA-18: reines Datum, keine Uhrzeit — eine Fehlzeit gilt für ganze Tage.
  // Ein Uhrzeit-Feld hätte hier nichts zu filtern und wurde vorher stillschweigend
  // verworfen.
  fields.push({ key: 'dateFrom', label: 'Von', type: 'date', category: 'core', operators: ['is'] })
  fields.push({ key: 'dateTo', label: 'Bis', type: 'date', category: 'core', operators: ['is'] })
  if (isManager) {
    fields.push({
      key: 'employee',
      label: 'Mitarbeiter',
      type: 'select-multi',
      category: 'core',
      options: users.map((u) => ({
        value: u.id,
        label: u.profile?.firstName
          ? `${u.profile.firstName}${u.profile.lastName ? ' ' + u.profile.lastName : ''}`
          : u.email ?? '—',
      })),
      operators: ['is', 'is_not'],
    })
  }
  fields.push({
    key: 'absenceType',
    label: 'Typ',
    type: 'select-multi',
    category: 'core',
    options: types.map((tp) => ({ value: tp.id, label: tp.name })),
    operators: ['is', 'is_not'],
  })
  fields.push({ key: 'note', label: 'Notiz', type: 'text', category: 'core', operators: ['contains', 'not_contains', 'is', 'is_not'] })
  fields.push({ key: 'durationHours', label: 'Dauer (Stunden)', type: 'number', category: 'core', operators: ['is', 'is_not', 'gt', 'lt'] })
  return fields
}

function employeeName(a: Absence): string {
  if (!a.user) return a.userId
  const p = a.user.profile
  return p?.firstName || p?.lastName ? `${p?.firstName ?? ''} ${p?.lastName ?? ''}`.trim() : a.user.email
}

/** Klartext einer Zelle – für Sortierung und CSV-Export. */
function cellText(a: Absence, key: string, hoursFormat: HoursFormat = 'decimal'): string {
  switch (key) {
    case 'date': return a.date.slice(0, 10)
    case 'employee': return employeeName(a)
    case 'absenceType': return a.absenceType?.name ?? ''
    case 'duration': return formatMinutes(a.durationMinutes, hoursFormat)
    case 'note': return a.note ?? ''
    default: return ''
  }
}

// ─── Client-seitige Filterung ───────────────────────────────────────────────────

function matchesFilters(a: Absence, filterValues: Record<string, ActiveFilter>): boolean {
  for (const [key, active] of Object.entries(filterValues)) {
    if (!active.value) continue
    if (key === 'dateFrom') { if (a.date.slice(0, 10) < active.value.slice(0, 10)) return false; continue }
    if (key === 'dateTo') { if (a.date.slice(0, 10) > active.value.slice(0, 10)) return false; continue }
    if (key === 'employee') {
      const ids = active.value.split(',').filter(Boolean)
      if (ids.length === 0) continue
      const hit = ids.includes(a.userId)
      if (active.operator === 'is_not' ? hit : !hit) return false
      continue
    }
    if (key === 'absenceType') {
      const ids = active.value.split(',').filter(Boolean)
      if (ids.length === 0) continue
      const hit = ids.includes(a.absenceTypeId)
      if (active.operator === 'is_not' ? hit : !hit) return false
      continue
    }
    if (key === 'note') {
      const cell = (a.note ?? '').toLowerCase()
      const val = active.value.toLowerCase()
      if (active.operator === 'contains' && !cell.includes(val)) return false
      if (active.operator === 'not_contains' && cell.includes(val)) return false
      if (active.operator === 'is' && cell !== val) return false
      if (active.operator === 'is_not' && cell === val) return false
      continue
    }
    if (key === 'durationHours') {
      const cellNum = a.durationMinutes / 60
      const filterNum = parseFloat(active.value.replace(',', '.'))
      if (!Number.isFinite(filterNum)) continue
      if (active.operator === 'gt' && !(cellNum > filterNum)) return false
      if (active.operator === 'lt' && !(cellNum < filterNum)) return false
      if (active.operator === 'is' && cellNum !== filterNum) return false
      if (active.operator === 'is_not' && cellNum === filterNum) return false
      continue
    }
  }
  return true
}

// ─── Dialog-State ───────────────────────────────────────────────────────────────

interface DialogState {
  open: boolean
  editing: Absence | null
  userId: string
  absenceTypeId: string
  date: string
  hours: string
  note: string
  /** Sobald der Nutzer die Stundenzahl selbst ändert, kein Auto-Vorschlag mehr. */
  hoursTouched: boolean
}

const EMPTY_DIALOG: DialogState = {
  open: false, editing: null, userId: '', absenceTypeId: '', date: todayStr(), hours: '8', note: '', hoursTouched: false,
}

// AP-19: Plausible Vorgabe-Stundenzahl aus den zuletzt geladenen Fehlzeiten des
// Zielmitarbeiters ableiten. Bevorzugt denselben Fehlzeit-Typ; nimmt den häufigsten
// Stundenwert (Modus), bei Gleichstand den jüngsten. null → keine Basis vorhanden.
function suggestAbsenceHours(rows: Absence[], userId: string, typeId: string): string | null {
  const pool = rows.filter((a) => a.userId === userId && a.durationMinutes > 0)
  if (pool.length === 0) return null
  const byRecent = [...pool].sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0))
  const sameType = typeId ? byRecent.filter((a) => a.absenceTypeId === typeId) : []
  const relevant = sameType.length > 0 ? sameType : byRecent
  const counts = new Map<number, number>()
  for (const a of relevant) counts.set(a.durationMinutes, (counts.get(a.durationMinutes) ?? 0) + 1)
  let bestMin = relevant[0].durationMinutes
  let bestCount = 0
  for (const [min, n] of counts) if (n > bestCount) { bestMin = min; bestCount = n }
  return String(bestMin / 60)
}

export default function AbsencesPage() {
  const hoursFormat = useHoursFormat()
  const authUser = useAuthStore((s) => s.user)
  // AP-30-D: „Sieht er fremde Fehlzeiten?" ist eine Frage nach dem
  // Geltungsbereich, nicht nach der Rolle — `time.absence.read.all` gegen
  // `.own`. Die Rollenliste, die hier stand, ging an „Nur Lesen" vorbei: Es
  // sieht alle Fehlzeiten und stand in keiner der drei Rollen.
  const isManager = erfuelltRecht('time.absence.read.all', authUser?.permissions)
  // „Alles sehen, nichts schreiben" ist jetzt der Normalfall und kein Sonderfall
  // mehr: ein fehlendes Schreibrecht.
  const canWrite = erfuelltRecht('time.absence.manage', authUser?.permissions)
  // KA-39: Fremde Fehlzeiten anlegen, ändern und löschen hängt an der Fremdbuchung — wie beim Server.
  const darfFuerAndere = erfuelltRecht('time.booking.for_others', authUser?.permissions)
  const darfZeile = (a: Absence) => canWrite && (a.userId === authUser?.id || darfFuerAndere)

  const [rows, setRows] = useState<Absence[]>([])
  const [total, setTotal] = useState(0)
  const [types, setTypes] = useState<AbsenceType[]>([])
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  // Filter
  // AP-40 6.4: der Zeitraum aus der Adresse (Kachel des Dashboards) — einmal beim Öffnen gelesen.
  const [suchParameter] = useSearchParams()
  const [adresse] = useState(() =>
    filterAusAdresse(suchParameter, [
      { key: 'dateFrom', label: '', type: 'date', category: 'core', operators: ['is'] },
      { key: 'dateTo', label: '', type: 'date', category: 'core', operators: ['is'] },
    ]),
  )
  const [filterValues, setFilterValues] = useState<Record<string, ActiveFilter>>({})

  // Spaltenlayout
  const availableColumns = buildAvailableColumns(isManager)
  const [columnLayout, setColumnLayout] = useState<ColumnLayout>(() => loadColumnLayout())
  const [columnConfigMode, setColumnConfigMode] = useState(false)
  const [addColDropdownOpen, setAddColDropdownOpen] = useState(false)
  const addColRef = useRef<HTMLDivElement>(null)
  const columnWidths = columnLayout.columnWidths ?? {}
  const sortConfig = columnLayout.sortConfig ?? null

  // Paginierung (clientseitig)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState<number>(() => {
    const v = loadUiSetting<number>(PAGE_SIZE_KEY, 50)
    return Number.isFinite(v) && v > 0 ? v : 50
  })

  // DnD + Resize
  const draggedKeyRef = useRef<string | null>(null)
  const [dragOverKey, setDragOverKey] = useState<string | null>(null)
  const resizeRef = useRef<{ key: string; startX: number; startWidth: number } | null>(null)

  const [dialog, setDialog] = useState<DialogState>(EMPTY_DIALOG)

  // ── Daten laden ───────────────────────────────────────────────────────────────
  const fetchRows = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await absencesApi.list({ limit: LOAD_LIMIT, sortDir: 'desc' })
      setRows(res.data.data)
      setTotal(res.data.meta.total)
    } catch (err) {
      console.error('Failed to load absences', err)
      setError('Fehlzeiten konnten nicht geladen werden.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    absenceTypesApi.list({ isActive: true, limit: 200 })
      .then((res) => setTypes(res.data.data))
      .catch((err) => console.error('Failed to load absence types', err))
    if (isManager || darfFuerAndere) {
      usersApi.list({ limit: 100, kind: 'EMPLOYEE' })
        .then((res) => setUsers(res.data.data))
        .catch((err) => console.warn('Could not load users', err))
    }
  }, [isManager, darfFuerAndere])

  useEffect(() => { fetchRows() }, [fetchRows])

  // Persist Spaltenlayout
  // AP-23: `…IfChanged` — dieser Effekt läuft auch beim Mount, und das bloße
  // Öffnen der Liste darf keine eigene Einstellung anlegen (sonst griffe der
  // Mandanten-Standard für diesen Benutzer nie wieder).
  useEffect(() => { saveColumnLayout(columnLayout) }, [columnLayout])

  // Seite bei Filter-/Sortieränderung zurücksetzen
  useEffect(() => { setPage(1) }, [filterValues, sortConfig, pageSize])

  // Add-Column-Dropdown bei Außenklick schließen
  useEffect(() => {
    if (!addColDropdownOpen) return
    const handler = (e: MouseEvent) => {
      if (addColRef.current && !addColRef.current.contains(e.target as Node)) setAddColDropdownOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [addColDropdownOpen])

  // Resize-Handler (document-level)
  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!resizeRef.current) return
      const delta = e.clientX - resizeRef.current.startX
      const newWidth = Math.max(60, resizeRef.current.startWidth + delta)
      setColumnLayout((prev) => ({
        ...prev,
        columnWidths: { ...(prev.columnWidths ?? {}), [resizeRef.current!.key]: newWidth },
      }))
    }
    const onUp = () => { resizeRef.current = null; document.body.style.cursor = '' }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
    return () => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
    }
  }, [])

  // ── Spalten-Helfer ──────────────────────────────────────────────────────────
  const shownColumns = columnLayout.columnKeys
    .map((k) => availableColumns.find((c) => c.key === k))
    .filter(Boolean) as ColumnFieldDef[]

  const availableToAddColumns = availableColumns.filter((c) => !columnLayout.columnKeys.includes(c.key))

  const groupedAvailableColumns = availableToAddColumns.reduce<Record<string, ColumnFieldDef[]>>((acc, c) => {
    if (!acc[c.category]) acc[c.category] = []
    acc[c.category].push(c)
    return acc
  }, {})

  const handleAddColumn = (key: string) => {
    setColumnLayout((prev) => ({ ...prev, columnKeys: [...prev.columnKeys, key] }))
    setAddColDropdownOpen(false)
  }
  const handleRemoveColumn = (key: string) => {
    setColumnLayout((prev) => ({ ...prev, columnKeys: prev.columnKeys.filter((k) => k !== key) }))
  }

  // ── Drag & Drop ─────────────────────────────────────────────────────────────
  const handleDragStart = (key: string) => { draggedKeyRef.current = key }
  const handleDragOver = (e: React.DragEvent, key: string) => {
    e.preventDefault()
    if (draggedKeyRef.current && draggedKeyRef.current !== key) setDragOverKey(key)
  }
  const handleDrop = (targetKey: string) => {
    const srcKey = draggedKeyRef.current
    if (!srcKey || srcKey === targetKey) { setDragOverKey(null); return }
    setColumnLayout((prev) => {
      const keys = [...prev.columnKeys]
      const from = keys.indexOf(srcKey)
      const to = keys.indexOf(targetKey)
      if (from < 0 || to < 0) return prev
      keys.splice(from, 1)
      keys.splice(to, 0, srcKey)
      return { ...prev, columnKeys: keys }
    })
    draggedKeyRef.current = null
    setDragOverKey(null)
  }
  const handleDragEnd = () => { draggedKeyRef.current = null; setDragOverKey(null) }

  // ── Sortierung (clientseitig) ─────────────────────────────────────────────────
  const handleSort = (key: string) => {
    if (!SORTABLE_KEYS.has(key)) return
    setColumnLayout((prev) => {
      const current = prev.sortConfig
      let next: ColumnSortConfig | null
      if (!current || current.key !== key) next = { key, dir: 'asc' }
      else if (current.dir === 'asc') next = { key, dir: 'desc' }
      else next = null
      return { ...prev, sortConfig: next }
    })
  }

  // ── Resize ────────────────────────────────────────────────────────────────────
  const handleResizeStart = (e: React.MouseEvent, key: string) => {
    e.preventDefault()
    e.stopPropagation()
    const th = (e.target as HTMLElement).closest('th')
    const startWidth = th ? th.getBoundingClientRect().width : (columnWidths[key] ?? 120)
    resizeRef.current = { key, startX: e.clientX, startWidth }
    document.body.style.cursor = 'col-resize'
  }

  // ── Filterung + Sortierung ──────────────────────────────────────────────────
  const filteredRows = useMemo(
    () => rows.filter((a) => matchesFilters(a, filterValues)),
    [rows, filterValues],
  )

  const sortedRows = useMemo(() => {
    if (!sortConfig) return filteredRows
    const { key, dir } = sortConfig
    const factor = dir === 'asc' ? 1 : -1
    return [...filteredRows].sort((a, b) => {
      if (key === 'duration') return (a.durationMinutes - b.durationMinutes) * factor
      if (key === 'date') return (a.date < b.date ? -1 : a.date > b.date ? 1 : 0) * factor
      return cellText(a, key, hoursFormat).localeCompare(cellText(b, key, hoursFormat), 'de', { sensitivity: 'base' }) * factor
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO(AP-08): Abhaengigkeiten pruefen; Ergaenzen kann zusaetzliche Laeufe ausloesen
  }, [filteredRows, sortConfig])

  const totalPages = Math.max(1, Math.ceil(sortedRows.length / pageSize))
  const currentPage = Math.min(page, totalPages)
  const displayRows = sortedRows.slice((currentPage - 1) * pageSize, currentPage * pageSize)

  // ── Summenzeile ────────────────────────────────────────────────────────────────
  const totalMinutes = filteredRows.reduce((s, a) => s + a.durationMinutes, 0)
  const columnTotals: Record<string, string> = { duration: formatMinutes(totalMinutes, hoursFormat) }

  // ── Export / Druck ──────────────────────────────────────────────────────────────
  const handleExportCsv = () => {
    const cols = [{ key: 'date', label: 'Datum' }, ...shownColumns.map((c) => ({ key: c.key, label: c.label }))]
    const headers = cols.map((c) => c.label)
    const dataRows = sortedRows.map((a) => cols.map((c) => (c.key === 'date' ? fmtDateLong(a.date) : cellText(a, c.key, hoursFormat))))
    const csv = [headers, ...dataRows]
      .map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(';'))
      .join('\n')
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `fehlzeiten_${todayStr()}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }
  const handlePrint = () => window.print()

  // ── Dialog ────────────────────────────────────────────────────────────────────
  const openCreate = () => {
    const typeId = types[0]?.id ?? ''
    const suggestion = authUser ? suggestAbsenceHours(rows, authUser.id, typeId) : null
    setDialog({ ...EMPTY_DIALOG, open: true, absenceTypeId: typeId, date: todayStr(), hours: suggestion ?? '8' })
  }
  const openEdit = (a: Absence) => {
    setDialog({
      open: true, editing: a, userId: a.userId, absenceTypeId: a.absenceTypeId,
      date: a.date.slice(0, 10), hours: String(a.durationMinutes / 60), note: a.note ?? '',
      // Bestehender Wert bleibt maßgeblich – kein Auto-Vorschlag im Bearbeiten-Modus.
      hoursTouched: true,
    })
  }
  const closeDialog = () => setDialog(EMPTY_DIALOG)

  // AP-19: Stunden-Vorschlag aktualisieren, wenn im Erfassen-Dialog Typ oder
  // Mitarbeiter wechseln – solange der Nutzer den Wert nicht selbst gesetzt hat.
  useEffect(() => {
    if (!dialog.open || dialog.editing || dialog.hoursTouched || !authUser) return
    const effectiveUser = dialog.userId || authUser.id
    const suggestion = suggestAbsenceHours(rows, effectiveUser, dialog.absenceTypeId)
    if (suggestion !== null) setDialog((d) => ({ ...d, hours: suggestion }))
  }, [dialog.open, dialog.editing, dialog.hoursTouched, dialog.userId, dialog.absenceTypeId, rows, authUser])

  const handleSave = async () => {
    const hoursNum = parseFloat(dialog.hours.replace(',', '.'))
    if (!dialog.absenceTypeId) { setError('Bitte einen Fehlzeit-Typ wählen.'); return }
    if (!dialog.date) { setError('Bitte ein Datum wählen.'); return }
    if (!Number.isFinite(hoursNum) || hoursNum <= 0) { setError('Bitte eine gültige Stundenzahl eingeben.'); return }
    const durationMinutes = Math.round(hoursNum * 60)
    setSaving(true)
    setError(null)
    try {
      if (dialog.editing) {
        await absencesApi.update(dialog.editing.id, {
          absenceTypeId: dialog.absenceTypeId,
          date: dialog.date,
          durationMinutes,
          note: dialog.note.trim() || undefined,
        })
      } else {
        await absencesApi.create({
          ...(darfFuerAndere && dialog.userId ? { userId: dialog.userId } : {}),
          absenceTypeId: dialog.absenceTypeId,
          date: dialog.date,
          durationMinutes,
          note: dialog.note.trim() || undefined,
        })
      }
      closeDialog()
      fetchRows()
    } catch (err) {
      console.error('Failed to save absence', err)
      setError('Fehlzeit konnte nicht gespeichert werden.')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (a: Absence) => {
    if (!(await bestaetige('Diese Fehlzeit wirklich löschen?'))) return
    try {
      await absencesApi.delete(a.id)
      fetchRows()
    } catch (err) {
      console.error('Failed to delete absence', err)
      setError('Fehlzeit konnte nicht gelöscht werden.')
    }
  }

  const noTypes = types.length === 0
  const filterFields = buildAvailableFilterFields(isManager, users, types)
  // Datum (fix) + konfigurierbare Spalten + Aktionen (fix)
  const totalColCount = 1 + shownColumns.length + (canWrite ? 1 : 0)

  // Sortier-Indikator für einen Spaltenkopf
  const sortIcon = (key: string) => {
    if (!SORTABLE_KEYS.has(key)) return null
    if (sortConfig?.key === key) {
      return sortConfig.dir === 'asc'
        ? <ArrowUp className="h-3 w-3 text-primary" />
        : <ArrowDown className="h-3 w-3 text-primary" />
    }
    return <ArrowUpDown className="h-3 w-3 opacity-30" />
  }

  return (
    <div className={pageWide}>
      {/* Header */}
      <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Fehlzeiten</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {isManager ? 'Fehlzeiten aller Mitarbeiter erfassen und verwalten' : 'Deine Fehlzeiten erfassen und verwalten'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" onClick={handlePrint}
            className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground hover:bg-muted">
            <Printer size={16} /> <span className="hidden sm:inline">Drucken</span>
          </button>
          <button type="button" onClick={handleExportCsv} disabled={sortedRows.length === 0}
            className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed">
            <Download size={16} /> <span className="hidden sm:inline">CSV</span>
          </button>
          {canWrite && (
            <button
              type="button"
              onClick={openCreate}
              disabled={noTypes}
              title={noTypes ? 'Es sind noch keine Fehlzeit-Typen angelegt.' : undefined}
              className="inline-flex items-center gap-2 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Plus size={16} /> Fehlzeit erfassen
            </button>
          )}
        </div>
      </div>

      {noTypes && (
        <div className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-300">
          Es sind noch keine Fehlzeit-Typen angelegt. Ein Administrator kann sie unter „Zeiterfassung → Einrichtung → Fehlzeit-Typen" anlegen.
        </div>
      )}

      {total > rows.length && (
        <div className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-300">
          Es werden die neuesten {rows.length} von {total} Fehlzeiten angezeigt. Bitte grenze den Zeitraum ein.
        </div>
      )}

      {/* FilterBar */}
      <FilterBar
        availableFields={filterFields}
        value={filterValues}
        onChange={setFilterValues}
        layoutKey="absences"
        defaultFieldKeys={DEFAULT_FILTER_FIELD_KEYS}
        adresse={adresse}
      />

      {/* Tabelle */}
      <div className="relative rounded-lg border border-border bg-card shadow-sm overflow-hidden">
        {/* Zahnrad – Spalten konfigurieren */}
        <button type="button" onClick={() => setColumnConfigMode((v) => !v)}
          className={`absolute right-1.5 top-1.5 z-10 flex h-6 w-6 items-center justify-center rounded-md transition-colors ${
            columnConfigMode ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
          }`}
          title={columnConfigMode ? 'Fertig' : 'Spalten anpassen'}>
          {columnConfigMode ? <Check size={14} /> : <Settings size={14} />}
        </button>

        {/* Hilfe „?" – links neben dem Spalten-Zahnrad */}
        <HelpPopover topic="columns" className="absolute right-8 top-1.5" hilfe={HILFE_VERWEISE.fehlzeiten} />

        {/* AP-23 Phase D: Zurücksetzen / als Mandanten-Standard speichern */}
        {columnConfigMode && (
          <div className="border-b border-border bg-muted/30 px-3 py-2 pr-16">
            <LayoutDefaultActions
              settingKey={COLUMN_LAYOUT_KEY}
              currentValue={columnLayout}
                    onReset={(value) => {
                const next = (value as ColumnLayout | null) ?? {
                  columnKeys: DEFAULT_COLUMN_KEYS, columnWidths: {}, sortConfig: null,
                }
                markUiSettingApplied(COLUMN_LAYOUT_KEY, next)
                setColumnLayout(next)
              }}
            />
          </div>
        )}

        {error && (
          <div className="px-4 py-3 text-sm text-red-600 bg-red-50 dark:bg-red-950 dark:text-red-400 border-b border-border">{error}</div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-muted/50">
              <tr>
                {/* Fixe erste Spalte: Datum */}
                <th className="relative px-3 py-1.5 text-left font-medium text-foreground"
                    style={columnWidths['date'] ? { width: columnWidths['date'] } : undefined}>
                  <button type="button" onClick={() => handleSort('date')} className="flex items-center gap-1 hover:text-primary">
                    Datum {sortIcon('date')}
                  </button>
                  <div className="absolute right-0 top-0 h-full w-1.5 cursor-col-resize hover:bg-primary/40 active:bg-primary"
                       onMouseDown={(e) => handleResizeStart(e, 'date')} />
                </th>

                {/* Konfigurierbare Spalten */}
                {shownColumns.map((col) => (
                  <th key={col.key}
                    className={`relative select-none px-3 py-1.5 font-medium text-foreground
                      ${col.align === 'right' ? 'text-right' : 'text-left'}
                      ${columnConfigMode ? 'cursor-grab' : ''}
                      ${dragOverKey === col.key ? 'bg-primary/10 outline outline-2 outline-primary' : ''}`}
                    draggable={columnConfigMode}
                    onDragStart={columnConfigMode ? () => handleDragStart(col.key) : undefined}
                    onDragOver={columnConfigMode ? (e) => handleDragOver(e, col.key) : undefined}
                    onDrop={columnConfigMode ? () => handleDrop(col.key) : undefined}
                    onDragEnd={columnConfigMode ? handleDragEnd : undefined}
                    style={columnWidths[col.key] ? { width: columnWidths[col.key] } : undefined}>
                    <div className={`flex items-center gap-1 ${col.align === 'right' ? 'justify-end' : ''}`}>
                      {columnConfigMode ? (
                        <>
                          <span>{col.label}</span>
                          <button type="button" onClick={() => handleRemoveColumn(col.key)}
                            className="rounded p-0.5 text-destructive hover:text-destructive/80" title="Entfernen">✕</button>
                        </>
                      ) : (
                        <button type="button" onClick={() => handleSort(col.key)}
                          className={`flex items-center gap-1 ${SORTABLE_KEYS.has(col.key) ? 'hover:text-primary' : 'cursor-default'}`}>
                          {col.label} {sortIcon(col.key)}
                        </button>
                      )}
                    </div>
                    {!columnConfigMode && (
                      <div className="absolute right-0 top-0 h-full w-1.5 cursor-col-resize hover:bg-primary/40 active:bg-primary"
                           onMouseDown={(e) => handleResizeStart(e, col.key)} />
                    )}
                  </th>
                ))}

                {/* "+ Spalte" im Config-Modus */}
                {columnConfigMode && availableToAddColumns.length > 0 && (
                  <th className="px-2 py-1.5">
                    <div className="relative" ref={addColRef}>
                      <button type="button" onClick={() => setAddColDropdownOpen((v) => !v)}
                        className="rounded-md border border-dashed border-border px-2 py-1 text-xs text-muted-foreground hover:text-foreground">
                        + Spalte
                      </button>
                      {addColDropdownOpen && (
                        <div className="absolute right-0 top-8 z-50 max-h-72 min-w-[200px] overflow-y-auto rounded-md border border-border bg-background shadow-lg">
                          {Object.entries(groupedAvailableColumns).map(([cat, cols]) => (
                            <div key={cat}>
                              <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                                {COLUMN_CATEGORY_LABELS[cat] ?? cat}
                              </div>
                              {cols.map((c) => (
                                <button key={c.key} type="button" onClick={() => handleAddColumn(c.key)}
                                  className="flex w-full items-center px-4 py-2 text-sm text-foreground hover:bg-muted">
                                  {c.label}
                                </button>
                              ))}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </th>
                )}

                {/* Aktionen (fix, letzte Spalte) */}
                {canWrite && !columnConfigMode && <th className="px-3 py-1.5 text-right font-medium text-foreground">Aktionen</th>}
              </tr>
            </thead>

            <tbody>
              {loading && displayRows.length === 0 && (
                <tr><td colSpan={totalColCount} className="px-6 py-8 text-center text-muted-foreground">Laden…</td></tr>
              )}
              {!loading && displayRows.length === 0 && (
                <tr><td colSpan={totalColCount} className="px-6 py-8 text-center text-muted-foreground">
                  <CalendarOff className="mx-auto mb-2 h-6 w-6 opacity-40" />
                  {rows.length === 0 ? 'Keine Fehlzeiten erfasst.' : 'Keine Fehlzeiten für die aktuellen Filter.'}
                </td></tr>
              )}

              {displayRows.map((a, idx) => (
                <tr key={a.id} className={`border-b border-border last:border-0 ${idx % 2 === 0 ? 'bg-background' : 'bg-muted/20'} hover:bg-accent/30 transition-colors`}>
                  {/* Datum (fix) */}
                  <td className="px-3 py-1.5 whitespace-nowrap tabular-nums text-muted-foreground">{fmtDateLong(a.date)}</td>
                  {/* Konfigurierbare Spalten */}
                  {shownColumns.map((col) => (
                    <td key={col.key} className={`px-3 py-1.5 ${col.align === 'right' ? 'text-right tabular-nums' : ''} ${col.key === 'note' ? '' : 'whitespace-nowrap'}`}>
                      {col.key === 'absenceType' ? (
                        <span className="inline-flex items-center gap-2">
                          <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: a.absenceType?.color ?? 'hsl(var(--muted-foreground))' }} />
                          {a.absenceType?.name ?? '—'}
                        </span>
                      ) : col.key === 'note' ? (
                        a.note ? <span className="text-muted-foreground">{a.note}</span> : <span className="opacity-40">–</span>
                      ) : col.key === 'employee' ? (
                        employeeName(a)
                      ) : col.key === 'duration' ? (
                        formatMinutes(a.durationMinutes, hoursFormat)
                      ) : null}
                    </td>
                  ))}
                  {/* Aktionen */}
                  {canWrite && !columnConfigMode && (
                    <td className="px-3 py-1.5 whitespace-nowrap text-right">
                      {darfZeile(a) && (
                        <>
                          <button type="button" onClick={() => openEdit(a)} title="Bearbeiten"
                            className="mr-1 rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"><Pencil size={15} /></button>
                          <button type="button" onClick={() => handleDelete(a)} title="Löschen"
                            className="rounded p-1 text-destructive hover:bg-destructive/10"><Trash2 size={15} /></button>
                        </>
                      )}
                    </td>
                  )}
                </tr>
              ))}

              {/* Summenzeile */}
              {displayRows.length > 0 && (
                <tr className="border-t-2 border-border bg-muted/50 font-medium">
                  <td className="px-3 py-1.5 whitespace-nowrap text-muted-foreground">
                    {filteredRows.length === total ? `${filteredRows.length} Einträge` : `${filteredRows.length} / ${total} Einträge`}
                  </td>
                  {shownColumns.map((col) => (
                    <td key={col.key} className={`px-3 py-1.5 ${col.align === 'right' ? 'text-right tabular-nums' : ''}`}>
                      {columnTotals[col.key] ?? ''}
                    </td>
                  ))}
                  {canWrite && !columnConfigMode && <td />}
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Paginierung */}
        {sortedRows.length > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-4 py-2 text-sm">
            <div className="flex items-center gap-2 text-muted-foreground">
              <span>Zeilen pro Seite</span>
              <select value={pageSize} onChange={(e) => { const n = parseInt(e.target.value, 10); setPageSize(n); saveUiSetting(PAGE_SIZE_KEY, n) }}
                className="rounded-md border border-input bg-background px-2 py-1 text-foreground focus:outline-none focus:ring-2 focus:ring-ring">
                {[25, 50, 100, 200].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <div className="flex items-center gap-3 text-muted-foreground">
              <span>Seite {currentPage} / {totalPages}</span>
              <button type="button" disabled={currentPage <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="rounded-md border border-border px-2 py-1 disabled:opacity-40 hover:bg-muted">Zurück</button>
              <button type="button" disabled={currentPage >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                className="rounded-md border border-border px-2 py-1 disabled:opacity-40 hover:bg-muted">Weiter</button>
            </div>
          </div>
        )}
      </div>

      {/* Erfassungs-/Bearbeiten-Dialog */}
      {dialog.open && (
        <Modal isOpen onClose={closeDialog} size="md" className="max-w-md">
          <div className="p-5">
            <h2 className="mb-4 text-lg font-semibold text-foreground">
              {dialog.editing ? 'Fehlzeit bearbeiten' : 'Fehlzeit erfassen'}
            </h2>
            <div className="space-y-4">
              {darfFuerAndere && !dialog.editing && (
                <div>
                  <label className="mb-1 block text-sm font-medium text-foreground">Mitarbeiter</label>
                  <select value={dialog.userId} onChange={(e) => setDialog((d) => ({ ...d, userId: e.target.value }))}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring">
                    <option value="">Ich selbst</option>
                    {users.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.profile?.firstName ? `${u.profile.firstName}${u.profile.lastName ? ' ' + u.profile.lastName : ''}` : u.email}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">Fehlzeit-Typ</label>
                <select value={dialog.absenceTypeId} onChange={(e) => setDialog((d) => ({ ...d, absenceTypeId: e.target.value }))}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring">
                  <option value="" disabled>Bitte wählen…</option>
                  {types.map((tp) => <option key={tp.id} value={tp.id}>{tp.name}</option>)}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">Datum</label>
                <input type="date" value={dialog.date} onChange={(e) => setDialog((d) => ({ ...d, date: e.target.value }))}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring" />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">Stunden</label>
                <input type="number" min={0.25} step={0.25} value={dialog.hours}
                  onChange={(e) => setDialog((d) => ({ ...d, hours: e.target.value, hoursTouched: true }))}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring" />
                <p className="mt-1 text-xs text-muted-foreground">z.B. 8 für einen ganzen Tag, 2 für einen Arztbesuch.</p>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">Notiz (optional)</label>
                <textarea value={dialog.note} onChange={(e) => setDialog((d) => ({ ...d, note: e.target.value }))} rows={2}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring" />
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button type="button" onClick={closeDialog}
                className="rounded-md border border-border px-4 py-2 text-sm text-foreground hover:bg-muted">Abbrechen</button>
              <button type="button" onClick={handleSave} disabled={saving}
                className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-40">
                {saving ? 'Speichern…' : 'Speichern'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}
